import { hostsConfig } from 'init/config'

export const TYPE_PHOTO = 0
export const TYPE_VIDEO = 1

export const MAX_MEDIA = 5
export const MAX_FILE_SIZE_MB = 10
export const MAX_FILE_SIZE = (MAX_FILE_SIZE_MB * 1024 * 1024)

export const ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/png',
  'image/x-png'
]

// assumption here is that the mime types represent actual file types
export const ACCEPTED_FILE_TYPES = ACCEPTED_MIME_TYPES.map((mimeType) => {
  return mimeType.substring(mimeType.indexOf('/') + 1)
})

export function getFileTypeFromName(fileName: string) {
  return fileName.toLowerCase().substring(fileName.lastIndexOf('.') + 1)
}

// media - the media object
// sizeSuffix - (_PZ320, _L1000, etc).  Miust include the underscore.  Can set empty string to
//              get the original image
export function getImageUrl(media, sizeSuffix = '') {
  const baseUrl = `${hostsConfig.protocol}://${hostsConfig.imageStoreBase}`
  const fileName = `${media.imageId}${sizeSuffix ? '_' + sizeSuffix : ''}.${media.imageType}`
  return `${baseUrl}/media/${hostsConfig.siteKey}/${fileName}`
}

/**
 * Get the orientation of an image file (jpeg).
 * The promise return value is the orientation.
 * If no orientation then
 * -1: not defined
 * -2: not a jpeg
 *
 * Function taken from:
 * https://stackoverflow.com/questions/7584794/accessing-jpeg-exif-rotation-data-in-javascript
 * -on-the-client-side
 * Orientations: https://i.stack.imgur.com/VGsAj.gif
 *
 * @param file: File
 */
export function getOrientation(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const view = new DataView(reader.result as ArrayBuffer)
      if (view.getUint16(0, false) !== 0xFFD8) {
        resolve(-2)
        return
      }
      const length = view.byteLength
      let offset = 2
      while (offset < length) {
        if (view.getUint16(offset + 2, false) <= 8) {
          resolve(-1)
          return
        }
        const marker = view.getUint16(offset, false)
        offset += 2
        if (marker === 0xFFE1) {
          if (view.getUint32(offset += 2, false) !== 0x45786966) {
            resolve(-1)
            return
          }

          const little = view.getUint16(offset += 6, false) === 0x4949
          offset += view.getUint32(offset + 4, little)
          const tags = view.getUint16(offset, little)
          offset += 2
          for (let i = 0; i < tags; i++) {
            if (view.getUint16(offset + (i * 12), little) === 0x0112) {
              resolve(view.getUint16(offset + (i * 12) + 8, little))
              return
            }
          }
        } else if ((marker & 0xFF00) !== 0xFF00) {
          break
        } else {
          offset += view.getUint16(offset, false)
        }
      }
      return resolve(-1)
    }
    reader.readAsArrayBuffer(file)
  })
}

export function getOrientationTransform(orientation: number) {
  switch (orientation) {
    case 2:
      return 'transform: scaleX(-1);'
    case 3:
      return 'transform: rotate(180deg);'
    case 4:
      return 'transform: rotate(180deg) scaleX(-1);'
    case 5:
      return 'transform: rotate(270deg) scaleX(-1);'
    case 6:
      return 'transform: rotate(90deg);'
    case 7:
      return 'transform: rotate(90deg) scaleX(-1);'
    case 8:
      return 'transform: rotate(270deg);'
    default:
      return ''
  }
}
